/**
 * What a platform administrator may do, split by AREA and by read/write.
 *
 * Plan: docs/plans/2026-08-04_PLATFORM_ADMIN_REDESIGN.md phase 0 (decision D2).
 *
 * ── What this replaces ──────────────────────────────────────────────────
 *
 * Until now platform authority was a single boolean: `users.platform_role`,
 * projected from membership of `Papan Admins`, where EVERY role conferred it.
 * A `viewer` of that org could re-price the catalog and change the credit
 * markup exactly like an `owner`. The role column rendered in the members UI
 * and meant nothing here — a restriction that looked real and was not.
 *
 * That was a deliberate interim call while the list was small. It stops being
 * defensible the moment the console can CREATE tenants, CHANGE plans and EDIT
 * platform-wide settings, which is what the redesign adds. So the role a person
 * already holds in `Papan Admins` now decides what they may do.
 *
 * ── The model ───────────────────────────────────────────────────────────
 *
 * `platform_role` remains the single ENFORCEMENT authority for "is this caller
 * a platform admin at all?" — `requirePlatformAdmin`, the `/api/admin/*` outer
 * gate and the SPA signal are untouched. This adds a SECOND, narrower question
 * on top: "…and may they do THIS?". Both must pass. A capability can only ever
 * narrow the existing grant, never widen it, which is what makes this change
 * safe to land before the screens that depend on it.
 *
 * ── Why the mapping is keyed by `RoleId` ────────────────────────────────
 *
 * `Record<RoleId, …>` is exhaustive, so adding a role to `CAP_ROLES` fails to
 * COMPILE here rather than silently landing in whichever bucket the lookup
 * happens to default to. That is the same reasoning as
 * `PLATFORM_ADMIN_CONFERRING_ROLES` deriving from `CAP_ROLES` instead of
 * restating it, and `__tests__/platform-capabilities.test.ts` pins the
 * resulting matrix so a change has to be argued for rather than absorbed.
 *
 * Browser-safe and dependency-free, in `@papan/types`, because the SPA needs
 * the same matrix to decide which nav items to render and `shared/core-ui` may
 * not import `@papan/auth` (that boundary is one-way). The server is still the
 * only enforcement point; the client copy exists so we do not offer a door that
 * is locked.
 */
import { capAtLeast, type RoleId } from './capabilities.js'

/**
 * The four areas of the platform console, matching its four destinations.
 *
 * Gate by what an action DOES, not where it renders. The per-tenant
 * feature-pricing override is the case that makes the distinction real: it
 * lives on the customer detail page but it is re-pricing, so it belongs to
 * `pricing`. Filing it under `customers` because of its location would hand
 * exactly the authority this split exists to withhold to everyone who can
 * administer a customer.
 */
export type PlatformArea = 'customers' | 'pricing' | 'operations' | 'settings'

/** Access level within one area. Same ladder as the org `access` value-set. */
export type PlatformAccess = 'none' | 'read' | 'write'

export const PLATFORM_AREAS: ReadonlyArray<{
  id: PlatformArea
  label: string
  hint: string
}> = [
  { id: 'customers', label: 'Customers', hint: 'Tenants, their caps, limits, models, credits and people' },
  { id: 'pricing', label: 'Price book', hint: 'Plans, credit packs, feature pricing — including per-tenant overrides' },
  { id: 'operations', label: 'Operations', hint: 'Dunning, webhooks, expensive sessions, the platform audit log' },
  { id: 'settings', label: 'Platform settings', hint: 'Deployment-wide configuration, the credit rate and the grace cap' },
]

/** Every area at one access level — the shape of a resolved grant. */
export type PlatformCapabilities = Record<PlatformArea, PlatformAccess>

const ALL_NONE: PlatformCapabilities = {
  customers: 'none', pricing: 'none', operations: 'none', settings: 'none',
}

const ALL_WRITE: PlatformCapabilities = {
  customers: 'write', pricing: 'write', operations: 'write', settings: 'write',
}

const ALL_READ: PlatformCapabilities = {
  customers: 'read', pricing: 'read', operations: 'read', settings: 'read',
}

/**
 * Role held in `Papan Admins` → what it may do on the platform console.
 *
 * The shape of the decision: `member` now carries the SAME full-write grant as
 * `owner`/`admin` — everyone who can administer the platform above the watching
 * tier may re-price the catalog and edit deployment-wide settings. `viewer` and
 * `guest` remain read-only across every area.
 *
 * This deliberately collapses the earlier `member` split (which held `pricing`
 * and `settings` at read only, to separate "administer customers" from
 * "re-price the product"). On this deployment a `member` seat in `Papan Admins`
 * is trusted with the full console; the `viewer`/`guest` tier is the only
 * read-only boundary that remains.
 *
 * `noaccess` is deliberately absent — it is not in `CAP_ROLES`, and its absence
 * is what gives an in-band REVOKE: park someone at `noaccess` and both their
 * `platform_role` projection and every capability here are gone on the next
 * request, with the seat and its audit trail preserved.
 */
export const PLATFORM_ROLE_CAPABILITIES: Readonly<Record<RoleId, PlatformCapabilities>> = {
  owner: ALL_WRITE,
  admin: ALL_WRITE,
  member: ALL_WRITE,
  viewer: ALL_READ,
  guest: ALL_READ,
}

/** Is `role` one the capability matrix knows about? */
function isCapRole(role: string | null | undefined): role is RoleId {
  return !!role && role in PLATFORM_ROLE_CAPABILITIES
}

/**
 * Resolve what this caller may do on the platform console.
 *
 * `isBootstrapAdmin` is NOT a convenience — leaving it out is a lockout.
 * `PLATFORM_ADMIN_EMAILS` grants `platform_role='admin'` on its own, without
 * requiring any membership of the platform org (`projectPlatformAdmin` ORs the
 * allowlist in, deliberately, so a membership mistake cannot lock every
 * administrator out with no in-band recovery). Such a caller has NO role in the
 * org, so a role-only resolution would hand them `none` on every area — turning
 * the ops-config root of trust into the one identity that can see nothing. The
 * allowlist therefore resolves to full write, which also keeps it strictly the
 * most powerful identity, as a root of trust should be.
 */
export function resolvePlatformCapabilities(input: {
  /** The caller's role in `Papan Admins`, or null/undefined if not a member. */
  role: string | null | undefined
  /** Is the caller in `PLATFORM_ADMIN_EMAILS`? */
  isBootstrapAdmin?: boolean
}): PlatformCapabilities {
  if (input.isBootstrapAdmin) return ALL_WRITE
  if (!isCapRole(input.role)) return ALL_NONE
  return PLATFORM_ROLE_CAPABILITIES[input.role]
}

/**
 * Does a resolved grant satisfy `required` on `area`?
 *
 * Uses the shared `capAtLeast` ladder rather than its own comparison, so
 * "write implies read" means the same thing here as it does for every org
 * capability. An out-of-set value fails closed.
 */
export function platformCan(
  caps: PlatformCapabilities | null | undefined,
  area: PlatformArea,
  required: Exclude<PlatformAccess, 'none'>,
): boolean {
  if (!caps) return false
  return capAtLeast(caps[area] ?? 'none', required, 'access')
}

/** Does this grant allow reaching the console at all? */
export function hasAnyPlatformAccess(caps: PlatformCapabilities | null | undefined): boolean {
  if (!caps) return false
  return PLATFORM_AREAS.some((a) => caps[a.id] !== 'none')
}
